import { makeStyles } from '@material-ui/core';
import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import theme from 'theme/theme';
import { statusColor, formattedDateRange, statusTitle, getToday } from '../utils';
import { useDebounce, usePrevious } from 'hooks';
import { useGetCurrentUserQuery, useUpdateForecastMutation, useCreateForecastMutation } from 'utils/wemble-api';
import RemoveButton from '../components/RemoveButton';
import organization from 'utils/organization';
import { getStatusSource } from 'utils/getStatusSource';

const useStyles = makeStyles(() => ({
  daterange: {
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'center',
    color: theme.palette.gray.g1
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
  },
  status: {
    fontWeight: 600,
    fontSize: '20px',
    textAlign: 'center',
    lineHeight: '120%',
    maxWidth: 100,
    overflowWrap: 'break-word'
  },
  source: {
    fontSize: '10px',
  },
  commentContainer:
  {
    width: 'calc(100% + 24px)',
    marginBottom: '-12px',
    padding: '8px',
    overflow: 'visible',
    position: 'relative',
  },
  commentBox: {
    backgroundColor: '#FFFFFF',
    width: '100%',
    borderRadius: 8,
    fontWeight: 500,
    textAlign: 'center',
    marginTop: 'auto',
    overflowWrap: 'break-word',
    overflow: 'visible',
    color: '#333333',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.1s ease',
  },
  commentInput: {
    padding: '6px 10px',
    fontWeight: 500,
    textAlign: 'center',
    background: 'none',
    border: 'none',
    color: '#333333',
    lineHeight: '120%',
    resize: 'none',
    fontFamily: 'inherit',
    overflow: 'hidden',
    width: '100%',
    '&:focus': {
      outline: 'none',
    },
    '&::placeholder': {
      color: '#999999',
    }
  },
  removeButton: {
    position: 'absolute',
    top: '-18px',
    right: '0px',
    opacity: 0,
    transition: 'opacity 0.1s ease',
  },
  commentBoxHoverArea: {
    position: 'absolute',
    top: '-20px',
    right: '0px',
    width: '100%',
    height: '20px',
    cursor: 'default',
  },
  characterCount: {
    position: 'absolute',
    top: '-16px',
    left: '0px',
    fontSize: '8px',
    lineHeight: '12px',
    color: '#828282',
    backgroundColor: '#FFFFFF',
    padding: '0px 2px',
    borderRadius: '4px',
    opacity: 0,
    transition: 'opacity 0.1s ease',
    cursor: 'default',
  },
}));

const getFontSize = (length: number) => {
  if (!length) return 12;
  if (length <= 20) return 11;
  if (length <= 35) return 10.5;
  if (length <= 50) return 10;
  if (length <= 70) return 9.5;
  if (length <= 85) return 9;
  return 8.5;
};

// Fit a textarea to its content (the text, or the placeholder when empty) at its current
// width. Resetting to auto first lets it shrink as well as grow.
const fitHeight = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
};

const DefaultView = ({
  data,
  today,
  onCommentHoverStart,
  onCommentHoverEnd
}: {
  data: { startDate?: string | undefined, endDate?: string | undefined, status?: string | undefined, comment?: string | null | undefined, externalSource?: string | null | undefined, _id?: string | null | undefined };
  today?: boolean;
  onCommentHoverStart?: () => void;
  onCommentHoverEnd?: () => void;
}) => {
  const classes = useStyles();
  const [updateForecast] = useUpdateForecastMutation();
  const [createForecast] = useCreateForecastMutation();
  const { data: user } = useGetCurrentUserQuery();

  const [comment, setComment] = useState<string | null>(data?.comment || null);
  const [debouncedComment] = useDebounce(comment, 150);
  const prevComment = usePrevious(debouncedComment);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // TODO: The refs below form a small ad-hoc state machine guarding effect-driven saves
  // against non-user-initiated changes (socket refreshes, id swaps, slot handovers). At
  // some point this should be rethought as event-driven saves: trigger the debounced save
  // from onChange instead of deriving it in an effect, which makes "only user edits get
  // saved" true by construction and removes most of these guards.
  //
  // Tracks the in-flight forecast creation for the synthetic today tile, and the last
  // comment value we sent to the server (to avoid redundant/echoed saves).
  const pendingCreateRef = useRef(false);
  const lastSavedCommentRef = useRef<string | null | undefined>(data?.comment ?? null);
  // True only after the user actually typed in the textarea — the save effect must never
  // fire from external data changes (date moves, id swaps, socket refreshes).
  const dirtyRef = useRef(false);
  const prevIdRef = useRef(data._id);

  // This component instance survives forecast transitions (the today slot has a stable key
  // in Forecasting). When a *different* forecast — or none — takes over the slot, adopt its
  // comment instead of leaking the previous one's local state. The create transitions
  // (no id → temp id → real id) keep local state so in-progress typing isn't clobbered.
  useEffect(() => {
    const prevId = prevIdRef.current;
    if (prevId === data._id) return;
    prevIdRef.current = data._id;
    const isCreateTransition = !prevId ||
      String(prevId).startsWith('temp-') || String(data._id || '').startsWith('temp-');
    if (isCreateTransition) return;
    setComment(data?.comment ?? null);
    lastSavedCommentRef.current = data?.comment ?? null;
    dirtyRef.current = false;
    pendingCreateRef.current = false;
  }, [data._id]);

  // Sync local comment state with data changes (from socket updates)
  useEffect(() => {
    if (data?.comment !== undefined && data.comment !== comment && !isFocused) {
      setComment(data.comment);
      lastSavedCommentRef.current = data.comment;
    }
  }, [data?.comment, comment, isFocused]);

  const maxCommentLength = organization(user?.company).expandComment ? 100 : 50;

  const removeComment = () => {
    setComment(null);
    lastSavedCommentRef.current = null;
    dirtyRef.current = false;
    if (data && data._id) {
      // Remove forecast comment
      updateForecast({
        userId: user?._id || '',
        forecastId: data._id || '',
        forecastUpsertParams: {
          startDate: data.startDate!,
          endDate: data.endDate!,
          status: data.status as "0" | "1" | "2" | "3" | "away",
          comment: null,
          sessionId: (window as any)._sessionId,
          source: getStatusSource()
        }
      });
    }
    // Note: If there's no forecast ID, we don't need to do anything for removal
    // The comment will just be cleared locally
  };


  const placeholder = today && !data._id ? "Comment for today..." : "Add comment...";

  useLayoutEffect(() => fitHeight(inputRef.current), [comment, placeholder]);

  // Cards narrow/widen as forecasts are added or removed (including right after the initial
  // load), so refit whenever the textarea's width changes. The refit is deferred a frame so
  // the observed element isn't resized inside its own ResizeObserver callback.
  // Re-subscribes if the textarea appears/disappears (externalSource).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    let width: number | undefined;
    let frame = 0;
    const observer = new ResizeObserver(entries => {
      const newWidth = entries[0].contentRect.width;
      if (newWidth === width) return;
      width = newWidth;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => fitHeight(el));
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [data.externalSource]);

  useEffect(() => {
    if (debouncedComment === undefined || prevComment === undefined) return;
    // Only user-typed edits get saved; external data changes never do.
    if (!dirtyRef.current) return;
    // Wait until the debounced value has caught up with the textarea.
    if (debouncedComment !== comment) return;
    if (debouncedComment === lastSavedCommentRef.current) {
      dirtyRef.current = false;
      return;
    }

    // The optimistic cache insert uses a temp id the server doesn't know about — hold off
    // while it's in place. When the real id lands, data._id changes and this effect re-runs,
    // pushing any edits made in the meantime.
    const isTempId = typeof data._id === 'string' && data._id.startsWith('temp-');

    if (data._id && !isTempId) {
      // Update existing forecast comment
      lastSavedCommentRef.current = debouncedComment;
      dirtyRef.current = false;
      updateForecast({
        userId: user?._id || '',
        forecastId: data._id || '',
        forecastUpsertParams: {
          startDate: data.startDate!,
          endDate: data.endDate!,
          status: data.status as "0" | "1" | "2" | "3" | "away",
          comment: debouncedComment,
          sessionId: (window as any)._sessionId,
          source: getStatusSource()
        }
      });
    } else if (today && !data._id && !pendingCreateRef.current && debouncedComment?.trim()) {
      // Synthetic today tile: the first saved comment creates a single-day forecast for
      // today. The tile keeps a stable key in Forecasting, so this doesn't remount the
      // textarea or steal focus.
      pendingCreateRef.current = true;
      lastSavedCommentRef.current = debouncedComment;
      dirtyRef.current = false;
      createForecast({
        userId: user?._id || '',
        forecastUpsertParams: {
          startDate: getToday(),
          endDate: getToday(),
          status: (data.status ?? '0') as "0" | "1" | "2" | "3" | "away",
          comment: debouncedComment,
          sessionId: (window as any)._sessionId,
          source: getStatusSource()
        }
      }).unwrap().catch(() => {
        // Creation failed and the optimistic insert was reverted — allow a retry
        pendingCreateRef.current = false;
      });
    }
  }, [comment, debouncedComment, data._id]);

  return (
    <>
      <div className={classes.daterange}>{formattedDateRange(data)}</div>
      <div className={classes.statusContainer} style={{ color: statusColor(data.status) }}>
        <div className={classes.status}>{statusTitle(data.status)}</div>
        {data.externalSource ? (<div className={classes.source}>From {data.externalSource}</div>) : ''}
      </div>
      {/* Show comment section for actual forecasts (has _id), external sources with a comment,
          and the synthetic today tile (where saving a comment creates a single-day forecast) */}
      {(data._id || today) && (!data.externalSource || data.comment) && (
        <div
          className={classes.commentContainer}
          onMouseEnter={() => onCommentHoverStart?.()}
          onMouseLeave={() => onCommentHoverEnd?.()}
        >
          <div
            className={classes.commentBox}
            style={{
              cursor: data.externalSource ? 'default' : 'pointer',
              transform: (isHovered || isFocused) ? 'scale(1.03)' : 'scale(1)'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className={classes.commentBoxHoverArea} />
            {!data.externalSource ? (
              <>
                <textarea
                  ref={inputRef}
                  className={classes.commentInput}
                  value={comment || ''}
                  onChange={(ev) => {
                    const newValue = ev.target.value;
                    if (newValue.length <= maxCommentLength) {
                      dirtyRef.current = true;
                      setComment(newValue);
                    }
                  }}
                  placeholder={placeholder}
                  onFocus={(e) => { e.stopPropagation(); setIsFocused(true); }}
                  onBlur={(e) => { e.stopPropagation(); setIsFocused(false); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  rows={1}
                  style={{
                    fontSize: getFontSize(comment?.length || 0),
                  }}
                />
                <RemoveButton
                  className={classes.removeButton}
                  variant="bright"
                  style={{ opacity: comment && isFocused ? 1 : 0 }}
                  onClick={(e) => { e.stopPropagation(); removeComment(); }}
                />
                <span
                  className={classes.characterCount}
                  style={{ opacity: isFocused && comment ? 1 : 0 }}
                >
                  {comment?.length || 0}/{maxCommentLength}
                </span>
              </>
            ) : (
              <span>{comment || ''}</span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DefaultView;
